import { getCourseContent, getCourseForContent, validateGlobalNavigation } from '../../../utils/progressUtils';

/**
 * Content navigation utility functions for Student Dashboard
 */

/**
 * Reset modal navigation to actual progress position
 * @param {Function} setModalNavigation - State setter for modal navigation
 * @param {Object} navigationContext - Navigation context from Redux
 */
export const resetToActualProgress = (setModalNavigation, navigationContext) => {
  setModalNavigation({
    viewingContent: null,
    viewingContentType: null,
    viewingIndex: -1,
    globalIndex: -1,
    viewingCourse: null,
    isViewingBackwards: false,
    allPathContent: navigationContext?.allPathContent || [],
    canNavigateBack: false,
    canNavigateForward: false
  });
};

/**
 * Get displayed content based on navigation state
 * @param {Object} modalNavigation - Modal navigation state
 * @param {Object} userCurrentContent - User's current content
 * @returns {Object} Content to display
 */
export const getDisplayedContent = (modalNavigation, userCurrentContent) => {
  return modalNavigation.isViewingBackwards
    ? modalNavigation.viewingContent
    : userCurrentContent;
};

/**
 * Get displayed content type based on navigation state
 * @param {Object} modalNavigation - Modal navigation state
 * @param {string} currentContentType - Current content type
 * @returns {string} Content type to display
 */
export const getDisplayedContentType = (modalNavigation, currentContentType) => {
  return modalNavigation.isViewingBackwards
    ? modalNavigation.viewingContentType
    : currentContentType;
};

/**
 * Get displayed content index based on navigation state
 * @param {Object} modalNavigation - Modal navigation state
 * @param {number} currentContentIndex - Current content index
 * @returns {number} Content index to display
 */
export const getDisplayedContentIndex = (modalNavigation, currentContentIndex) => {
  return modalNavigation.isViewingBackwards
    ? modalNavigation.viewingIndex
    : currentContentIndex;
};

/**
 * Handle backward navigation in content modal
 * @param {Object} modalNavigation - Current modal navigation state
 * @param {Object} navigationContext - Navigation context from Redux
 * @param {Array} pathCourses - Array of courses in the path
 * @param {Object} userProgress - User's progress data
 * @param {Function} setModalNavigation - State setter for modal navigation
 */
export const handleBackNavigation = (
  modalNavigation,
  navigationContext,
  pathCourses,
  userProgress,
  setModalNavigation
) => {
  const allPathContent = navigationContext?.allPathContent || [];
  const currentGlobalIndex = modalNavigation.isViewingBackwards ?
    modalNavigation.globalIndex :
    navigationContext?.currentGlobalIndex || -1;

  let newGlobalIndex = currentGlobalIndex - 1;

  // Skip reinforcement items when navigating backwards
  while (newGlobalIndex >= 0) {
    const content = allPathContent[newGlobalIndex];

    // Check if this content is a reinforcement
    if (content && content.type === 'reinforcement') {
      newGlobalIndex--;
      continue;
    }

    // Found non-reinforcement content, validate and navigate
    if (validateGlobalNavigation(newGlobalIndex, allPathContent, userProgress)) {
      const newContent = allPathContent[newGlobalIndex];
      const newCourse = getCourseForContent(newContent._id, pathCourses);

      // Validate course lookup succeeded
      if (!newCourse) {
        newGlobalIndex--;
        continue;
      }

      // Find local index within the new course
      const newCourseContent = getCourseContent(newCourse);
      const newLocalIndex = newCourseContent.findIndex(content => content._id === newContent._id);

      // Validate local index calculation succeeded
      if (newLocalIndex === -1) {
        newGlobalIndex--;
        continue;
      }

      // All validations passed - set navigation state
      const actualGlobalIndex = navigationContext?.currentGlobalIndex || -1;
      const isNowViewingBackwards = newGlobalIndex < actualGlobalIndex;

      setModalNavigation({
        viewingContent: newContent,
        viewingContentType: newContent.type,
        viewingIndex: newLocalIndex,
        globalIndex: newGlobalIndex,
        viewingCourse: newCourse,
        isViewingBackwards: isNowViewingBackwards,
        allPathContent: allPathContent,
        canNavigateBack: newGlobalIndex > 0,
        canNavigateForward: true
      });

      return; // Successfully navigated
    }

    // Content at this index is not navigable, try previous
    newGlobalIndex--;
  }

};


/**
 * Handle forward browsing in content modal
 * @param {Object} modalNavigation - Current modal navigation state
 * @param {Object} navigationContext - Navigation context from Redux
 * @param {Array} pathCourses - Array of courses in the path
 * @param {Object} userProgress - User's progress data
 * @param {Function} setModalNavigation - State setter for modal navigation
 * @param {Function} resetToActualProgressFn - Function to reset to actual progress
 */
export const handleForwardBrowsing = (
  modalNavigation,
  navigationContext,
  pathCourses,
  userProgress,
  setModalNavigation,
  resetToActualProgressFn
) => {
  const allPathContent = navigationContext?.allPathContent || [];
  const currentGlobalIndex = modalNavigation.globalIndex;
  const actualGlobalIndex = navigationContext?.currentGlobalIndex || -1;

  // If already at or past the actual progress position, reset to actual progress
  if (currentGlobalIndex >= actualGlobalIndex - 1) {
    resetToActualProgressFn();
    return;
  }

  let newGlobalIndex = currentGlobalIndex + 1;

  // Skip reinforcement items when navigating forward
  while (newGlobalIndex < allPathContent.length) {
    const content = allPathContent[newGlobalIndex];

    // Check if this content is a reinforcement
    if (content && content.type === 'reinforcement') {
      newGlobalIndex++;
      continue;
    }

    // Found non-reinforcement content
    // If we've reached or passed the actual progress, show "Return to Progress" button
    if (newGlobalIndex >= actualGlobalIndex - 1) {
      // Validate the content is navigable
      if (validateGlobalNavigation(newGlobalIndex, allPathContent, userProgress)) {
        const newContent = allPathContent[newGlobalIndex];
        const newCourse = getCourseForContent(newContent._id, pathCourses);

        // Find local index within the new course
        const newCourseContent = newCourse ? getCourseContent(newCourse) : [];
        const newLocalIndex = newCourseContent.findIndex(content => content._id === newContent._id);

        // Set navigation state - button will show "Return to Progress" when globalIndex >= actualGlobalIndex - 1
        const isNowViewingBackwards = newGlobalIndex < actualGlobalIndex;

        setModalNavigation({
          viewingContent: newContent,
          viewingContentType: newContent.type,
          viewingIndex: newLocalIndex,
          globalIndex: newGlobalIndex,
          viewingCourse: newCourse,
          isViewingBackwards: isNowViewingBackwards,
          allPathContent: allPathContent,
          canNavigateBack: newGlobalIndex > 0,
          canNavigateForward: false // Can't browse further forward, show "Return to Progress" instead
        });

        return; // Successfully navigated
      }
    }

    // Continue browsing forward within navigable content
    if (validateGlobalNavigation(newGlobalIndex, allPathContent, userProgress)) {
      const newContent = allPathContent[newGlobalIndex];
      const newCourse = getCourseForContent(newContent._id, pathCourses);

      // Find local index within the new course
      const newCourseContent = newCourse ? getCourseContent(newCourse) : [];
      const newLocalIndex = newCourseContent.findIndex(content => content._id === newContent._id);

      setModalNavigation({
        viewingContent: newContent,
        viewingContentType: newContent.type,
        viewingIndex: newLocalIndex,
        globalIndex: newGlobalIndex,
        viewingCourse: newCourse,
        isViewingBackwards: newGlobalIndex < actualGlobalIndex,
        allPathContent: allPathContent,
        canNavigateBack: newGlobalIndex > 0,
        canNavigateForward: newGlobalIndex < actualGlobalIndex - 1
      });

      return; // Successfully navigated
    }

    // Content at this index is not navigable, try next
    newGlobalIndex++;
  }

};


/**
 * Check if path is completed
 * @param {Array} pathCourses - Array of courses in the path
 * @param {Object} userProgress - User's progress data
 * @param {Function} getCompletedCoursesFn - Function to get completed courses
 * @returns {boolean} True if path is completed
 */
export const isPathCompleted = (pathCourses, userProgress, getCompletedCoursesFn) => {
  // First check backend flag
  if (userProgress?.isPathCompleted) {
    return true;
  }

  // Fallback: check if all courses are completed
  if (!pathCourses.length) return false;

  const completedCourses = getCompletedCoursesFn();
  const totalCourses = pathCourses.length;

  return completedCourses.length === totalCourses;
};